/**
 * @fileoverview Shell for the Privacy Policy/Terms & Conditions/Contact Support pages.
 * @description A header (logo, cross-linking nav tabs, language switcher) above the page's own
 * content, plus a minimal footer. Distinct from AuthLayout (which centers a single card on a
 * gradient background) since these are full-width reading pages reached from AuthLayout's own
 * footer links and ErrorStateCard's "Contact Support" link, not part of the sign-in flow itself.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { useTranslation } from 'react-i18next'
import { NavLink } from 'react-router'

import logoUrl from '@/assets/images/login/logo.png'
import { appConfig } from '@/config/app-config'
import LanguageSwitcher from '@/shared/components/molecules/LanguageSwitcher'
import { ROUTES } from '@/shared/constants/routes.constants'
import { SHOW_LANGUAGE_SWITCHER } from '@/shared/lib/i18n/language.constants'

import type { ReactNode } from 'react'

interface LegalLayoutProps {
  children: ReactNode
}

const NAV_TABS = [
  { path: ROUTES.PRIVACY_POLICY, labelKey: 'legalNav.privacyPolicy' },
  { path: ROUTES.TERMS_AND_CONDITIONS, labelKey: 'legalNav.termsAndConditions' },
  { path: ROUTES.CONTACT_SUPPORT, labelKey: 'legalNav.contactSupport' },
]

/**
 * @component
 * @param {LegalLayoutProps} props - The page content to render below the header.
 * @param {ReactNode} props.children - The page content to render below the header.
 */
function LegalLayout({ children }: LegalLayoutProps) {
  const { t } = useTranslation()

  return (
    <div className="min-h-screen bg-input-bg">
      <header className="border-b border-divider bg-white">
        <div className="mx-auto flex max-w-[880px] flex-wrap items-center justify-between gap-3.5 px-6 py-4.5">
          <img src={logoUrl} alt="InsightBox" className="h-8 w-auto" />
          <div className="flex flex-wrap items-center gap-3">
            <nav className="flex flex-wrap gap-1">
              {NAV_TABS.map((tab) => (
                <NavLink
                  key={tab.path}
                  to={tab.path}
                  className={({ isActive }) =>
                    `rounded-full px-3.5 py-1.5 text-[13px] font-semibold ${
                      isActive ? 'bg-primary/10 text-primary' : 'text-slate hover:bg-input-bg'
                    }`
                  }
                >
                  {t(tab.labelKey)}
                </NavLink>
              ))}
            </nav>
            {SHOW_LANGUAGE_SWITCHER && <LanguageSwitcher />}
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[880px] px-6 py-11 pb-20">{children}</main>

      <footer className="mx-auto max-w-[880px] px-6 pb-12 text-center text-xs text-footer">
        {appConfig.version} · © {appConfig.copyrightYear} {appConfig.name}
      </footer>
    </div>
  )
}

export default LegalLayout
